import { Link } from 'react-router';

import { Card, MonoLabel, cn, focusRing, serviceTone } from '@/design-system';
import { stopsFor, type PlanningRow, type PlanningWeek } from '@/entities/order';

import { paths } from '@/app/router/paths';
import { planningCopy } from '../copy';

export interface WeekGridProps {
    week: PlanningWeek;
    rows: readonly PlanningRow[];
}

/**
 * Het weekrooster: één rij per inspecteur, een kolom per werkdag. Onder 780 px
 * schuift de kaart horizontaal - nooit samenpersen, nooit clippen (principe 11).
 *
 * De blokjes dragen de **dienstkleur** (`border-left: 3px`), niet de
 * statuskleur. Een blokje opent het dossier van die keuring.
 */
export function WeekGrid({ week, rows }: WeekGridProps) {
    const template = `132px repeat(${week.days.length}, minmax(0, 1fr))`;

    return (
        <Card clip className="overflow-x-auto overscroll-x-contain">
            <div role="table" aria-label={planningCopy.gridLabel} style={{ minWidth: 780 }}>
                <div role="rowgroup">
                    <div
                        role="row"
                        className="grid border-b border-[var(--border-default)] bg-[var(--surface-subtle)]"
                        style={{ gridTemplateColumns: template }}
                    >
                        <div
                            role="columnheader"
                            className="px-[14px] py-3 text-[12px] text-[var(--text-tertiary-safe)]"
                        >
                            {planningCopy.columnInspector}
                        </div>
                        {week.days.map((day) => (
                            <div
                                role="columnheader"
                                key={day.date}
                                className="border-l border-[var(--border-subtle)] px-[14px] py-3"
                            >
                                <div className="text-[13px] font-medium text-[var(--text-primary)]">
                                    {day.label}
                                </div>
                                <MonoLabel size={10} tone="muted" as="div" className="mt-[2px]">
                                    {planningCopy.kilometres(day.distanceKm)}
                                </MonoLabel>
                            </div>
                        ))}
                    </div>
                </div>

                <div role="rowgroup">
                    {rows.map((row) => (
                        <div
                            role="row"
                            key={row.inspectorUuid}
                            className="grid min-h-[92px] border-b border-[var(--border-subtle-2)] bg-[var(--surface-card)]"
                            style={{ gridTemplateColumns: template }}
                        >
                            <div
                                role="rowheader"
                                className="flex flex-col gap-[3px] px-[14px] py-3"
                            >
                                <span className="text-[13px] font-medium text-[var(--text-primary)]">
                                    {row.name}
                                </span>
                                <MonoLabel size={10} tone="muted">
                                    {planningCopy.rowSummary(row.stopCount, row.distanceKm)}
                                </MonoLabel>
                            </div>

                            {week.days.map((day) => {
                                const cell = row.cells[day.date];
                                const stops = stopsFor(row, day.date);

                                return (
                                    <div
                                        role="cell"
                                        key={day.date}
                                        aria-label={`${row.name} · ${day.label}`}
                                        className={cn(
                                            'flex flex-col gap-[5px] border-l border-[var(--border-subtle)] p-[7px]',
                                            cell && !cell.available && 'bg-[var(--surface-subtle)]',
                                        )}
                                    >
                                        {cell && !cell.available && stops.length === 0 ? (
                                            <span className="text-[11px] text-[var(--text-tertiary-safe)]">
                                                {planningCopy.unavailable}
                                            </span>
                                        ) : null}
                                        {stops.map((stop) => (
                                            <Link
                                                key={stop.orderUuid}
                                                to={paths.dossier(stop.orderUuid)}
                                                aria-label={planningCopy.stopLabel(
                                                    stop.time,
                                                    stop.place,
                                                    stop.orderNumber,
                                                )}
                                                className={cn(
                                                    'block rounded-[6px] px-2 py-[7px] no-underline',
                                                    focusRing,
                                                )}
                                                style={{
                                                    borderLeft: `3px solid ${serviceTone[stop.service].bar}`,
                                                    background: serviceTone[stop.service].bg,
                                                }}
                                            >
                                                <MonoLabel
                                                    size={10}
                                                    variant="value"
                                                    tone="default"
                                                    as="div"
                                                >
                                                    {stop.time}
                                                </MonoLabel>
                                                <div className="mt-[2px] text-[12px] leading-[1.25] font-medium text-[var(--text-primary)]">
                                                    {stop.place}
                                                </div>
                                                <div className="mt-[1px] truncate text-[11px] text-[var(--text-tertiary-safe)]">
                                                    {stop.serviceLabel}
                                                </div>
                                            </Link>
                                        ))}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        </Card>
    );
}
